import { TFunction } from 'i18next';

// NOTE: array ordering matters for text display arrangement

// read type scopes that always displayed at the beginning of the sentence
const alwaysComeFirstScopes = ['life_insurance_read'];

// list of scopes that reads account informations
const accountInfoScopes = [
  'accounts_read',
  'points_read',
  'investment_accounts_read'
];

// list of scopes that reads account transactions
const accountTransactionsScopes = [
  'transactions_read',
  // NOTE: points use the same scopes for reading "transactions"
  'points_read',
  'investment_transactions_read'
];

// other read type scopes
const otherReadScopes = ['expense_claims_read', 'categories_read'];

function excludeArray(mainArray: string[], removeArray: string[]) {
  const removeArraySet = new Set(removeArray);

  return mainArray.filter((scope) => !removeArraySet.has(scope));
}

export function excludeReadScopes(fullScopes: string[]) {
  return excludeArray(fullScopes, [
    ...alwaysComeFirstScopes,
    ...accountInfoScopes,
    ...accountTransactionsScopes,
    ...otherReadScopes
  ]);
}

function aggregateScopesIntoSections(t: TFunction, scopes: string[]) {
  const first = new Set();
  const balancesTransactions = new Set();
  const balances = new Set();
  const transactions = new Set();
  const others = new Set();

  for (const scope of scopes) {
    const text = t(`SSOOnboardFormContent.dataAccess.scopes.${scope}`);

    // scopes that will display at the beginning of the sentence
    if (alwaysComeFirstScopes.includes(scope)) {
      first.add(text);
      continue;
    }

    // ##
    // aggregate scopes that requires connected account access details about
    // - informations/balances
    // - transactions
    // - both
    if (accountInfoScopes.includes(scope)) {
      if (transactions.has(text)) {
        balancesTransactions.add(text);
        transactions.delete(text);
        continue;
      }

      balances.add(text);
    }

    if (accountTransactionsScopes.includes(scope)) {
      if (balances.has(text)) {
        balancesTransactions.add(text);
        balances.delete(text);
        continue;
      }

      transactions.add(text);
    }
    // ##

    // other scopes that appeared at the end of the sentence
    if (otherReadScopes.includes(scope)) {
      others.add(text);
    }
  }

  return { first, balancesTransactions, balances, transactions, others };
}

export default function readScopesDescription(
  t: TFunction,
  scopes: string[]
): string {
  const { first, balancesTransactions, balances, transactions, others } =
    aggregateScopesIntoSections(t, scopes);

  const comma = t('common.comma');
  // use array for ordering sentence display order
  const connectedSentence = [
    Array.from(first).join(comma),
    balancesTransactions.size &&
      t('SSOOnboardFormContent.dataAccess.connected.subSentence', {
        sentence: t(
          'SSOOnboardFormContent.dataAccess.connected.balancesTransactions'
        ),
        scopes: Array.from(balancesTransactions).join(comma)
      }),
    balances.size &&
      t('SSOOnboardFormContent.dataAccess.connected.subSentence', {
        sentence: t('SSOOnboardFormContent.dataAccess.connected.balances'),
        scopes: Array.from(balances).join(comma)
      }),
    transactions.size &&
      t('SSOOnboardFormContent.dataAccess.connected.subSentence', {
        sentence: t('SSOOnboardFormContent.dataAccess.connected.transaction'),
        scopes: Array.from(transactions).join(comma)
      })
  ]
    // filter out falshy values from the result of no content needed for certain sections
    .filter((v) => v)
    .join(comma);

  // prepare description for scopes access on connected account if available
  const connectedDescription =
    connectedSentence &&
    t('SSOOnboardFormContent.dataAccess.connected.baseSentence', {
      sentence: connectedSentence
    });

  // prepare description for scopes access on created content if available
  const createdDescription =
    (others.size &&
      t('SSOOnboardFormContent.dataAccess.created.baseSentence', {
        sentence: Array.from(others).join(comma)
      })) ||
    undefined;

  // nothing to display
  if (!connectedDescription && !createdDescription) {
    return '';
  }

  // merge all sentences into full sentences based on their availbility
  return t('SSOOnboardFormContent.dataAccess.baseSentence', {
    connected: connectedDescription,
    created: createdDescription,
    and: connectedDescription && createdDescription && t('common.and')
  });
}
